import {Component, computed, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import QRCode from 'qrcode';
import {CompanyDataService} from '@services/company-data.service';
import {CompanyService} from '@services/company.service';
import {WalletService} from '@services/wallet.service';
import {UserStateService} from '@services/frontend/user-state.service';
import {ICompanyDetails} from '@models/company.interface';
import {ICompteAdministration} from '@models/compteAdministration.interface';
import {NotificationService} from '@services/frontend/notification.service';
import {httpErrorMessage} from 'app/utils/http-error.utils';

@Component({
  selector: 'app-company-wallet',
  templateUrl: './company-wallet.html',
})
export class CompanyWallet implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly companyData = inject(CompanyDataService);
  private readonly companyService = inject(CompanyService);
  private readonly walletService = inject(WalletService);
  private readonly notificationService = inject(NotificationService);
  private readonly userState = inject(UserStateService);

  compte: WritableSignal<ICompteAdministration | null> = signal(null);
  details: WritableSignal<ICompanyDetails> = signal({} as ICompanyDetails);
  mobilePlatform = signal<'ios' | 'android' | null>(null);
  qrDataUrl = signal<string | null>(null);
  qrLoading = signal(false);
  qrLabel = signal<string>('Google Wallet');
  googleLoading = signal(false);
  appleLoading = signal(false);
  pushLoading = signal(false);

  private siret = '';

  isAdmin = computed(() => !!this.userState.getUser()()?.admin);
  isDesktop = computed(() => this.mobilePlatform() === null);
  showQr = computed(() => this.isDesktop() && (this.qrLoading() || this.qrDataUrl() !== null));

  /** Decorative QR mosaic for the Apple-style preview (not a real barcode). */
  readonly qrPlaceholderCells = [
    1, 1, 1, 0, 1,
    1, 0, 1, 0, 1,
    1, 1, 1, 1, 0,
    0, 1, 0, 1, 1,
    1, 0, 1, 1, 1,
  ];

  /** Back-of-pass style rows shown under the card (Apple Wallet details). */
  backRows = computed(() => {
    const d = this.details();
    const siret = this.compte()?.company?.siret;
    const rows: { label: string; value: string }[] = [
      { label: 'Siren', value: d.sirenFormate || d.siren || '' },
      { label: 'Siret', value: siret || '' },
      { label: 'N° TVA', value: d.numeroTvaIntracommunautaire || '' },
      { label: 'APE', value: d.codeNaf || '' },
      { label: 'Dirigeant', value: d.dirigeant || '' },
      { label: 'Adresse', value: d.adresseSiege || '' },
      { label: 'Forme juridique', value: d.formeJuridique || '' },
      { label: 'RCS', value: d.numeroRcs || d.statutRcs || '' },
      { label: 'Capital', value: d.capitalFormate || '' },
      { label: 'Activité', value: d.domaineActivite || '' },
      { label: 'Effectif', value: d.effectif || '' },
      { label: 'Inscription RCS', value: d.statutRcs || '' },
      { label: 'Inscription RNE', value: d.statutRne || '' },
    ];
    return rows.filter(r => r.value && r.value !== '-');
  });

  docLinks = computed(() => {
    const c = this.compte()?.company;
    if (!c) {
      return [] as { label: string }[];
    }
    return [
      { label: 'Extrait KBIS', id: c.idKbis },
      { label: 'Attestation Vigilance URSSAF', id: c.idAttestationVigilanceUrssaf },
      { label: 'Attestation Régularité fiscale', id: c.idAttestationRegulariteFiscale },
      { label: 'Avis CFE', id: c.idAvisCfe },
    ].filter(d => !!d.id).map(d => ({ label: d.label }));
  });

  ngOnInit() {
    this.siret = this.route.parent!.snapshot.paramMap.get('siret')!;
    this.mobilePlatform.set(this.detectMobile());
    this.companyData.load(this.siret).subscribe(c => {
      this.compte.set(c);
      this.details.set(c.company.companyDetailResponse);
    });
  }

  private detectMobile(): 'ios' | 'android' | null {
    const ua = navigator.userAgent || '';
    if (!/Mobi|Android|iPhone|iPad|iPod/i.test(ua)) {
      return null;
    }
    if (/iPad|iPhone|iPod/i.test(ua)) {
      return 'ios';
    }
    if (/Android/i.test(ua)) {
      return 'android';
    }
    return null;
  }

  downloadApplePass() {
    if (this.mobilePlatform() === 'android') {
      this.notificationService.error('Apple Wallet disponible sur iPhone ou iPad.', 4000);
      return;
    }
    this.appleLoading.set(true);
    this.walletService.getAppleWalletPassDownloadUrl(this.siret).subscribe({
      next: async (url) => {
        this.appleLoading.set(false);
        if (this.isDesktop()) {
          await this.showQrCode(url, 'Apple Wallet');
          this.notificationService.success('Scannez le QR Code avec votre iPhone', 3500);
          return;
        }
        window.location.href = url;
      },
      error: (err) => {
        this.appleLoading.set(false);
        this.notificationService.error(httpErrorMessage(err, 'Apple Wallet indisponible.'), 5000);
      },
    });
  }

  openGoogleWallet() {
    if (!this.isAdmin()) {
      this.notificationService.error('Google Wallet arrive bientôt.', 4000);
      return;
    }
    if (this.mobilePlatform() === 'ios') {
      this.notificationService.error('Google Wallet disponible sur Android.', 4000);
      return;
    }
    this.googleLoading.set(true);
    this.walletService.getGoogleWalletPassDownloadUrl(this.siret).subscribe({
      next: async (url) => {
        this.googleLoading.set(false);
        if (this.isDesktop()) {
          await this.showQrCode(url, 'Google Wallet');
          this.notificationService.success('Scannez le QR Code avec votre téléphone Android', 3500);
          return;
        }
        window.location.href = url;
      },
      error: (err) => {
        this.googleLoading.set(false);
        this.notificationService.error(httpErrorMessage(err, 'Google Wallet indisponible.'), 6000);
      },
    });
  }

  pushWalletUpdate() {
    if (!this.isAdmin() || this.pushLoading()) {
      return;
    }
    this.pushLoading.set(true);
    this.companyService.pushWalletUpdate(this.siret).subscribe({
      next: () => {
        this.pushLoading.set(false);
        this.notificationService.success(
          'Mise à jour envoyée (Apple + Google : format et données).',
          3500
        );
      },
      error: (err) => {
        this.pushLoading.set(false);
        this.notificationService.error(
          httpErrorMessage(err, 'Erreur lors de la mise à jour du Wallet.'),
          4000
        );
      },
    });
  }

  private async showQrCode(url: string, label: string) {
    this.qrLoading.set(true);
    this.qrLabel.set(label);
    try {
      const dataUrl = await QRCode.toDataURL(url, {
        width: 280,
        margin: 2,
        errorCorrectionLevel: 'M',
        color: {dark: '#123C33', light: '#FFFFFF'},
      });
      this.qrDataUrl.set(dataUrl);
    } catch {
      this.qrDataUrl.set(null);
      this.notificationService.error('Impossible de générer le QR Code', 3000);
    } finally {
      this.qrLoading.set(false);
    }
  }

  copyField(label: string, value: string | null | undefined) {
    if (!value || value === '—') {
      return;
    }
    void navigator.clipboard.writeText(value).then(() => {
      this.notificationService.success(`${label} copié`, 1200);
    });
  }

  copyAll() {
    const details = this.details();
    const content = [
      `Entreprise: ${details.nomEntreprise || '—'}`,
      `Dirigeant: ${details.dirigeant || '—'}`,
      `SIREN: ${details.sirenFormate || details.siren || '—'}`,
      `Capital: ${details.capitalFormate || '—'}`,
      `SIRET: ${this.compte()?.company?.siret || '—'}`,
      `APE: ${details.codeNaf || '—'}`,
      `N° TVA: ${details.numeroTvaIntracommunautaire || '—'}`,
      `Adresse: ${details.adresseSiege || '—'}`,
    ].join('\n');
    void navigator.clipboard.writeText(content).then(() => {
      this.notificationService.success('Toutes les informations ont été copiées', 1800);
    });
  }

  share() {
    const details = this.details();
    const text = [
      details.nomEntreprise || 'Mon entreprise',
      `Dirigeant: ${details.dirigeant || '—'}`,
      `SIRET: ${this.compte()?.company?.siret || '—'}`,
      `SIREN: ${details.sirenFormate || details.siren || '—'}`,
      `N° TVA: ${details.numeroTvaIntracommunautaire || '—'}`,
    ].join('\n');

    if (navigator.share) {
      void navigator.share({
        title: `${details.nomEntreprise || 'Mon entreprise'} — Carte Attestio`,
        text
      });
      return;
    }

    void navigator.clipboard.writeText(text).then(() => {
      this.notificationService.success('Informations partagées (copiées dans le presse-papiers)', 1800);
    });
  }
}
